<template>
  <div class="my-payouts-view">
    <h1 class="page-title">My Payouts</h1>
    
    <div class="summary-container">
      <div class="payout-summary">
        <div class="card">
          <h2 v-if="availableAmount > 0">
            You've got <span class="amount">${{ availableAmount.toFixed(2) }}</span> waiting for you!
          </h2>
          <h2 v-else>
            Total Earned: <span class="amount">${{ paidAmount.toFixed(2) }}</span>!
          </h2>
          <p v-if="!hasCompletedAssessment" class="instruction">
            Complete the steps below to collect your gift card
          </p>
          
          <div class="steps">
            <!-- Step 1: Email confirmation (always completed) -->
            <div class="step completed">
              <div class="step-number">1</div>
              <div class="step-content">
                <h3>Confirm your email</h3>
                <p>Your email address has been confirmed during registration.</p>
                
                <div class="email-input-group">
                  <input v-model="userEmail" type="email" disabled class="disabled-input" />
                </div>

                <p class="success-text">Email confirmed!</p>
              </div>
            </div>

            <!-- Step 2: Complete at least one assessment -->
            <div class="step" :class="{ 'completed': hasCompletedAssessment }">
              <div class="step-number">2</div>
              <div class="step-content">
                <h3>Complete at least one assessment</h3>
                <p v-if="!hasCompletedAssessment">Complete your first product packaging assessment to unlock gift card rewards.</p>
                <p v-else-if="availableAmount > 0">Great! You've {{ pendingPaymentsCount }} pending payment{{ pendingPaymentsCount !== 1 ? 's' : '' }}</p>
                <p v-else>Great! You've completed {{ completedAssessmentsCount }} assessment(s). Complete more assessments to earn gift cards!</p>

                <button v-if="hasCompletedAssessment && availableAmount > 0" class="btn primary" @click="sendGiftCard" :disabled="sendingGiftCard">
                  <span v-if="sendingGiftCard" class="spinner"></span>
                  {{ sendingGiftCard ? 'Sending...' : 'Send gift card to my email' }}
                </button>

                <p v-if="hasCompletedAssessment" class="success-text">Assessment completed!</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <div class="payments-table">
      <h2>My Payments</h2>
      <v-card elevation="1" class="payments-table-card">
        <DataTable
          :headers="tableHeaders"
          :items="payments"
          :loading="loading"
          :showPagination="!!(payments && payments.length > 5)"
          :enableSorting="true"
          :itemsPerPage="5"
          noDataText="No payments found"
          noDataSubtext="Payments will appear here after you complete assessments"
          class="payments-table"
        >
          <!-- Status Column -->
          <template #column-status="{ item }">
            <v-chip
              small
              :color="getStatusColor(item.status)"
              text-color="white"
              label
            >
              {{ getStatusLabel(item.status) }}
            </v-chip>
          </template>
          
          <!-- Date Column -->
          <template #column-created_at="{ item }">
            {{ formatDate(item.created_at) }}
          </template>
        </DataTable>
      </v-card>
    </div>
  </div>
</template>

<script>
import apiService from '@/services/apiService';
import DataTable from '@/components/DataTable.vue';
import { useToast } from 'vue-toastification';

export default {
  name: 'MyPayoutsView',
  components: {
    DataTable
  },
  setup() {
    const toast = useToast();
    return { toast };
  },
  data() {
    return {
      loading: true,
      availableAmount: 0,
      paidAmount: 0,
      hasCompletedAssessment: false,
      completedAssessmentsCount: 0,
      pendingPaymentsCount: 0,
      userEmail: '',
      sendingGiftCard: false,
      payments: [],
      tableHeaders: [
        { text: 'Amount', value: 'amount', sortable: true },
        { text: 'Currency', value: 'currency', sortable: true },
        { text: 'Status', value: 'status', sortable: true },
        { text: 'Date', value: 'created_at', sortable: true }
      ]
    };
  },
  created() {
    this.fetchPayoutSummary();
  },
  methods: {
    async fetchPayoutSummary() {
      try {
        this.loading = true;
        const response = await apiService.get('/projects/payouts/summary/');
        
        this.availableAmount = response.data.available_amount;
        this.paidAmount = response.data.paid_amount;
        this.hasCompletedAssessment = response.data.has_completed_assessment;
        this.completedAssessmentsCount = response.data.completed_assessments_count;
        this.userEmail = response.data.user_email;
        this.payments = Array.isArray(response.data.payments) ? response.data.payments : [];
        
        // Calculate pending payments count
        this.pendingPaymentsCount = this.payments.filter(
          payment => ['pending', 'approved', 'review'].includes(payment.status)
        ).length;
      } catch (error) {
        console.error('Error fetching payout summary:', error);
      } finally {
        this.loading = false;
      }
    },
    
    async sendGiftCard() {
      try {
        this.sendingGiftCard = true;
        await apiService.post('/projects/payouts/send-gift-card/');
        this.toast.success('Gift card request sent! You will receive an email with your gift card shortly.');
        
        // Refresh the payout summary data since payment info has changed
        await this.fetchPayoutSummary();
      } catch (error) {
        this.toast.error('Failed to send gift card request');
        console.error('Error sending gift card:', error);
      } finally {
        this.sendingGiftCard = false;
      }
    },
    
    getStatusColor(status) {
      switch (status.toLowerCase()) {
        case 'paid':
          return 'success';
        case 'pending':
          return 'warning';
        case 'approved':
          return 'primary';
        case 'review':
          return 'info'; // blue tone for "Under review"
        case 'failed':
        case 'rejected':
          return 'error';
        default:
          return 'grey';
      }
    },
    
    capitalizeFirstLetter(string) {
      if (!string) return '';
      return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
    },
    
    getStatusLabel(status) {
      if (!status) return '';
      switch (status.toLowerCase()) {
        case 'review':
          return 'Under review';
        default:
          return this.capitalizeFirstLetter(status);
      }
    },
    
    formatDate(dateString) {
      if (!dateString) return 'N/A';
      try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return 'N/A';
        return `${date.toLocaleString('default', { month: 'short' })} ${date.getDate()} ${date.getFullYear()}`;
      } catch (error) {
        console.error('Error formatting date:', error);
        return 'N/A';
      }
    }
  }
}
</script>

<style scoped>
.my-payouts-view {
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
}

.page-title {
  font-size: 2rem;
  font-weight: bold;
  margin-bottom: 1.5rem;
}

.summary-container {
  display: flex;
  gap: 2rem;
  margin-bottom: 2rem;
}

.payout-summary {
  flex: 3;
}

.rewards-info {
  flex: 1;
  min-width: 250px;
}

.card {
  background-color: #f7f9fc;
  border-radius: 8px;
  padding: 1.5rem;
  height: 100%;
}

.card h2 {
  font-size: 1.5rem;
  margin-bottom: 0.5rem;
}

.rewards-info h2 {
  font-size: 1.5rem;
  color: #e21351;
  margin-bottom: 0.5rem;
}

.amount {
  color: #e21351;
  font-weight: bold;
}

.instruction {
  color: #666;
  margin-bottom: 1.5rem;
}

.steps {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.step {
  display: flex;
  border-radius: 8px;
  padding: 1rem;
  background: white;
}

.step-number {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background-color: #e21351;
  color: white;
  font-weight: bold;
  margin-right: 1rem;
  flex-shrink: 0;
}

.step-content {
  flex: 1;
}

.step-content h3 {
  font-size: 1.1rem;
  margin-bottom: 0.5rem;
}

.step-content p {
  color: #666;
  margin-bottom: 1rem;
}

.step.completed .step-number {
  background-color: #22c55e;
}

.step.disabled {
  opacity: 0.6;
}

.btn {
  padding: 0.5rem 1rem;
  border-radius: 4px;
  border: none;
  cursor: pointer;
  font-weight: 500;
}

.btn.primary {
  background-color: #e21351;
  color: white;
}

.btn.primary:hover {
  background-color: #c01145;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.spinner {
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid transparent;
  border-top: 2px solid currentColor;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-right: 8px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.payments-table h2 {
  font-size: 1.3rem;
  margin-bottom: 1rem;
}

.payments-table {
  width: 100%;
  padding: 0 16px;
}

.surveys-table {
  width: 100%;
  border-collapse: collapse;
}

.surveys-table th,
.surveys-table td {
  padding: 0.75rem 1rem;
  text-align: left;
  border-bottom: 1px solid #eaeaea;
}

.surveys-table th {
  background-color: #f5f5f5;
  font-weight: 600;
}

.surveys-table tr:hover td {
  background-color: #f9f9f9;
}

/* Mobile responsive adjustments */
@media (max-width: 768px) {
  .summary-container {
    flex-direction: column;
  }
  
  .payout-summary,
  .rewards-info {
    flex: 1;
    width: 100%;
  }
}

/* additional styles for the new input group */
.email-input-group {
  display: flex;
  gap: 0.5rem;
  align-items: center;
  margin-bottom: 0.5rem;
}

.email-input-group input {
  flex: 1;
  padding: 0.5rem 0.75rem;
  border: 1px solid #ccc;
  border-radius: 4px;
}

.disabled-input {
  background-color: #f5f5f5;
  color: #666;
  cursor: not-allowed;
}

.disabled-input:disabled {
  opacity: 0.8;
}

.success-text {
  color: #22c55e;
  font-weight: 500;
}

.payments-table-card {
  border-radius: 8px;
  overflow: hidden;
  background-color: white;
  box-shadow: 0px 2px 8px rgba(0, 0, 0, 0.05);
}

.payments-table-card :deep(table) {
  background-color: #f8f9fa;
  border-collapse: separate;
  border-spacing: 16px 8px;
}

.payments-table-card :deep(tr) {
  background-color: white;
  border-radius: 4px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  transition: background-color 0.2s, box-shadow 0.3s;
}

.payments-table-card :deep(tr:hover) {
  background-color: #f8f9fa;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
}

.payments-table-card :deep(td) {
  padding: 12px 16px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}

.payments-table-card :deep(th) {
  font-weight: 600 !important;
  color: rgba(0, 0, 0, 0.6) !important;
  padding: 16px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  text-transform: none;
  font-size: inherit;
}

.payments-table-card :deep(.v-data-table__wrapper) {
  min-height: auto;
}
</style> 